<template>
  <div class="page-container licenses-page">
    <portal to="page-name">Licenças</portal>

    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Gestão de Licenças</h1>
      </div>

      <div class="card-body">
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <template v-else>
          <div class="license-management-card">
            <div class="license-info-section">
              <div class="license-stats">
                <div class="stat-item">
                  <div class="stat-icon">
                    <fa-icon :icon="['fal', 'box']" />
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">Licenças Adquiridas</div>
                    <div class="stat-value">{{ licenses }}</div>
                  </div>
                </div>
                <div class="stat-item">
                  <div class="stat-icon text-primary">
                    <fa-icon :icon="['fal', 'users']" />
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">Licenças em Uso</div>
                    <div class="stat-value text-primary">
                      {{ licensesInUse }}
                    </div>
                  </div>
                </div>
                <div class="stat-item">
                  <div
                    class="stat-icon"
                    :class="licencesAvailable > 0 ? 'text-success' : 'text-warning'"
                  >
                    <fa-icon :icon="['fal', 'check-circle']" />
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">Licenças Livres</div>
                    <div
                      class="stat-value"
                      :class="licencesAvailable > 0 ? 'text-success' : 'text-warning'"
                    >
                      {{ licencesAvailable }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="license-control-section">
              <div class="control-header">
                <h6 class="control-title">Ajustar Quantidade de Licenças</h6>
              </div>
              <div class="control-actions">
                <st-input-number-step
                  id="license-quantity"
                  v-model="newQuantity"
                  :min-value="licensesInUse"
                  :readonly="saving"
                />
                <button
                  class="btn btn-primary"
                  @click="openConfirmationModal"
                  :disabled="newQuantity === licenses"
                >
                  <fa-icon :icon="['fal', 'save']" />
                  Salvar Configuração
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="card card-page user-list-card">
      <div class="card-header">
        <h1 class="card-title">Usuários Ativos</h1>
      </div>

      <div class="card-body">
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <template v-else>
            <template v-if="sessions.length === 0">
              <div class="empty">
                <div class="empty-icon">
                  <fa-icon :icon="['fal', 'users']" size="3x"/>
                </div>
                <p class="empty-title h5">Nenhum usuário ativo</p>
                <p class="empty-subtitle">
                  Não há usuários conectados no momento.
                </p>
              </div>
            </template>
            <template v-else>
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th class="hide-sm" style="width:30px">#</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Última atividade</th>
                    <th>IP</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(session, i) in sessions" :key="i">
                    <td class="hide-sm">{{ i + 1 }}</td>
                    <td>{{ session.entity.name }}</td>
                    <td>{{ session.entity.email }}</td>
                    <td>{{ session.lastActivityAt | date('datetime') }}</td>
                    <td>{{ session.ip }}</td>
                    <td class="text-right">
                      <button
                        class="btn btn-sm btn-action btn-icon btn-error tooltip tooltip-left"
                        data-tooltip="Desconectar usuário"
                        @click="disconnectUser(session)"
                      ><fa-icon :icon="['fal', 'sign-out-alt']" /></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
        </template>
      </div>
    </div>

    <st-license-confirmation-modal
      :show="confirmationModalVisible"
      :current-quantity="licenses"
      :new-quantity="newQuantity"
      :unit-price="unitPrice"
      :loading="saving"
      @close="confirmationModalVisible = false"
      @confirm="saveLicenseChanges"
    />
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      saving: false,
      licenses: 0,
      licensesInUse: 0,
      unitPrice: 0,
      newQuantity: 0,
      confirmationModalVisible: false,
      sessions: [],
    };
  },
  computed: {
    licencesAvailable() {
      return this.licenses - this.licensesInUse;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        await this.loadData();
        await this.loadSessions();
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    async loadData() {
      const { data } = await this.$http.get('/customer/licenses');
      this.licenses = data.licenses;
      this.newQuantity = data.licenses;
      this.unitPrice = data.unitPrice;
      this.licensesInUse = data.licensesInUse;
    },
    async loadSessions() {
      const { data } = await this.$http.get('/sessions');
      this.sessions = data.items;
    },
    openConfirmationModal() {
      if (this.newQuantity === this.licenses) {
        this.$toast.show('A quantidade não foi alterada.', { type: 'info' });
        return;
      }
      this.confirmationModalVisible = true;
    },
    async saveLicenseChanges() {
      this.saving = true;
      try {
        await this.$http.put('/customer/licenses', {
          licenses: this.newQuantity,
        });

        const difference = this.newQuantity - this.licenses;
        this.licenses = this.newQuantity;

        this.confirmationModalVisible = false;

        if (difference > 0) {
          this.$toast.success(`${difference} licença(s) adicionada(s) com sucesso!`);
        } else {
          this.$toast.success(`${Math.abs(difference)} licença(s) removida(s) com sucesso!`);
        }
      } catch (e) {
        this.$toast.error('Ocorreu um erro ao salvar as alterações. Tente novamente!');
      } finally {
        this.saving = false;
      }
    },
    async disconnectUser(session) {
      try {
        await this.$http.delete(`/sessions/${session.id}`);

        const index = this.sessions.findIndex(u => u.id === session.id);
        if (index > -1) {
          this.sessions.splice(index, 1);
          this.licensesInUse -= 1;
        }

        this.$toast.success(`Usuário ${session.entity.name} desconectado com sucesso!`);
      } catch (e) {
        this.$toast.error(e);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.licenses-page {
  .license-management-card {
    display: flex;
    flex-direction: column;
    gap: $layout-spacing-lg * 1.5;
  }

  .license-info-section {
    background: linear-gradient(
      135deg,
      lighten($primary-color, 45%) 0%,
      lighten($primary-color, 48%) 100%
    );
    border-radius: 12px;
    border: 1px solid lighten($primary-color, 40%);
  }

  .license-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: $layout-spacing-lg;

    @media (max-width: $size-md) {
      grid-template-columns: 1fr;
    }

    .stat-item {
      display: flex;
      align-items: center;
      gap: $layout-spacing;
      padding: $layout-spacing-lg;
      background: white;
      border-radius: 10px;
      border: 1px solid $border-color;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .stat-icon {
        font-size: 32px;
        color: $gray-color-dark;
        flex-shrink: 0;
      }

      .stat-content {
        flex: 1;
      }

      .stat-label {
        font-size: 13px;
        color: $gray-color-dark;
        margin-bottom: $layout-spacing-sm / 2;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.5px;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 700;
        color: $dark-color;
        line-height: 1;
      }
    }
  }

  .license-control-section {
    background: $bg-color-light;
    padding: $layout-spacing-lg;
    border-radius: 12px;
    border: 1px solid $border-color;

    .control-header {
      margin-bottom: $layout-spacing;

      .control-title {
        font-size: 16px;
        font-weight: 600;
        color: $dark-color;
        margin: 0;
      }
    }

    .control-actions {
      display: flex;
      align-items: center;
      gap: $layout-spacing;
      flex-wrap: wrap;

      @media (max-width: $size-sm) {
        flex-direction: column;
        align-items: stretch;
      }

      .btn-save {
        flex: 0 0 auto;
        padding: 0 $layout-spacing-lg;
        height: 40px;
        border-radius: 8px;

        @media (max-width: $size-sm) {
          width: 100%;
        }
      }
    }
  }


  .user-list-card {
    margin-top: $layout-spacing-lg;
  }

  .text-primary {
    color: $primary-color;
  }

  .text-success {
    color: $success-color;
  }

  .text-warning {
    color: $warning-color;
  }
}
</style>
